import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import dayjs from 'dayjs';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface HistoryRecord {
  id: string;
  timestamp: string;
  status: 'active' | 'inactive';
  health: string;
  message: string;
}

interface CCTVHistoryGraphProps {
  data: HistoryRecord[];
  deviceName?: string;
}

const CCTVHistoryGraph: React.FC<CCTVHistoryGraphProps> = ({ data, deviceName }) => {
  // Process data for charts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    // Sort data by timestamp
    const sortedData = [...data].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Group data by hour for timeline
    const hourlyData: Map<string, { active: number; inactive: number; total: number }> = new Map();

    sortedData.forEach((record) => {
      const hourKey = dayjs(record.timestamp).format('DD/MM HH:00');
      const existing = hourlyData.get(hourKey) || { active: 0, inactive: 0, total: 0 };
      existing.total++;
      if (record.status === 'active') {
        existing.active++;
      } else {
        existing.inactive++;
      }
      hourlyData.set(hourKey, existing);
    });

    const labels = Array.from(hourlyData.keys());
    const activeData = Array.from(hourlyData.values()).map((v) => v.active);
    const inactiveData = Array.from(hourlyData.values()).map((v) => v.inactive);
    const uptimeData = Array.from(hourlyData.values()).map((v) =>
      v.total > 0 ? (v.active / v.total) * 100 : 0
    );

    return {
      labels,
      activeData,
      inactiveData,
      uptimeData,
    };
  }, [data]);

  if (!chartData) {
    return (
      <div className="no-graph-data">
        <p>No data available for graph visualization.</p>
      </div>
    );
  }

  // Status Timeline Chart (Line)
  const statusTimelineData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Uptime %',
        data: chartData.uptimeData,
        fill: true,
        borderColor: '#27ae60',
        backgroundColor: 'rgba(39, 174, 96, 0.1)',
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: '#27ae60',
      },
    ],
  };

  const statusTimelineOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: `Camera Uptime Over Time${deviceName ? ` - ${deviceName}` : ''}`,
        font: { size: 14, weight: 'bold' },
      },
      tooltip: {
        callbacks: {
          label: (context) => `Uptime: ${(context.raw as number)?.toFixed(1)}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Uptime %',
        },
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
      x: {
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  // Status Distribution Chart (Bar)
  const statusBarData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Working',
        data: chartData.activeData,
        backgroundColor: 'rgba(39, 174, 96, 0.8)',
        borderColor: '#27ae60',
        borderWidth: 1,
      },
      {
        label: 'Non-Working',
        data: chartData.inactiveData,
        backgroundColor: 'rgba(231, 76, 60, 0.8)',
        borderColor: '#e74c3c',
        borderWidth: 1,
      },
    ],
  };

  const statusBarOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: 'Status Distribution Over Time',
        font: { size: 14, weight: 'bold' },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: true,
        title: {
          display: true,
          text: 'Count',
        },
      },
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  return (
    <div className="history-graphs">
      <div className="graph-grid">
        <div className="graph-card">
          <div className="graph-container" style={{ height: '300px' }}>
            <Line data={statusTimelineData} options={statusTimelineOptions} />
          </div>
        </div>
        <div className="graph-card">
          <div className="graph-container" style={{ height: '300px' }}>
            <Bar data={statusBarData} options={statusBarOptions} />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="graph-summary">
        <div className="summary-item">
          <span className="summary-label">Total Records</span>
          <span className="summary-value">{data.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Working States</span>
          <span className="summary-value success">
            {data.filter((r) => r.status === 'active').length}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Non-Working States</span>
          <span className="summary-value danger">
            {data.filter((r) => r.status === 'inactive').length}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Avg Uptime</span>
          <span className="summary-value">
            {data.length > 0
              ? ((data.filter((r) => r.status === 'active').length / data.length) * 100).toFixed(1)
              : 0}
            %
          </span>
        </div>
      </div>
    </div>
  );
};

export default CCTVHistoryGraph;
